import { useEffect, useRef, useState } from 'react'
import type { MapRef } from 'react-map-gl'
import { Outlet } from 'react-router'
import {
  aCheckedListings,
  aClient,
  aMapCurrentCount,
  aMapRef,
  aParcelDataVisible,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import {
  MapView,
  useCurrentMapView,
  useMapListings,
  useMapProperties,
  type MapOutletContextData,
} from '~/hooks/map'
import {
  CheckAllListings,
  ListingsMap,
  MapActions,
  MapCardsList,
  MapDrawControl,
  MapFilterSheet,
  MapLayersSheet,
  MapLegend,
  MapListingCountLoading,
  MapNavigationTabs,
  MapSearch,
  UserAvatar,
  MapViewControls,
} from './components'
import {
  ClientPolygon,
  MapBuildingsLayer,
  MapGeographiesLayer,
  // MapParcelsLayer,
  MapPropertyClustersLayer,
  ServeParcelsLayer,
} from './layers'

export const shouldRevalidate = () => false

export default function MapLayout() {
  return <MapViewLayout />
}

function MapViewLayout() {
  const mapRef = useRef<MapRef>(null)
  const setMapRef = useSetAtom(aMapRef)
  const currentMapCount = useAtomValue(aMapCurrentCount)
  const currentMapView = useCurrentMapView()
  const parcelDataVisible = useAtomValue(aParcelDataVisible)
  const client = useAtomValue(aClient)
  const checkedListings = useAtomValue(aCheckedListings)

  useEffect(() => {
    if (mapRef.current) {
      setMapRef(mapRef.current!)
    }
  }, [mapRef, setMapRef])

  const {
    listings,
    // TODO Handle error here or in the query like we do in associate?
    isError,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useMapListings()

  const { properties, totalCount, isLoadingProperties, isFetchingProperties } =
    useMapProperties()
  const [isLoaded, setIsLoaded] = useState(false)
  useEffect(() => setIsLoaded(true), [])

  if (!isLoaded) {
    return null
  }

  return (
    <main className="h-dvh w-dvw">
      <ListingsMap mapRef={mapRef}>
        <div className="absolute left-2 top-2 z-50 flex gap-2 sm:left-4 sm:top-4">
          <MapNavigationTabs />
          <MapActions className="hidden sm:block" />
          <MapDrawControl />
          {checkedListings.length > 0 && (
            <CheckAllListings
              listings={listings}
              currentMapCount={currentMapCount}
            />
          )}
        </div>
        <MapListingCountLoading
          className="absolute bottom-2 left-1/2 z-30 -translate-x-1/2 sm:bottom-4 lg:bottom-auto lg:top-4"
          isLoading={isLoadingProperties || isFetchingProperties}
          count={currentMapCount}
          totalCount={totalCount}
        />
        <div className="absolute right-14 top-2 flex gap-2 sm:right-16 sm:top-4">
          {currentMapView === MapView.Map && (
            <MapSearch className="hidden sm:block" />
          )}
          <MapLayersSheet client={client!} />
          <MapFilterSheet />
        </div>
        <div className="absolute right-2 top-2 flex flex-col gap-2 sm:right-4 sm:top-4">
          <UserAvatar />
          <MapViewControls />
        </div>
        <MapLegend className="hidden sm:block" />
        <ClientPolygon />
        {client.parcel_data_enabled && parcelDataVisible && <ServeParcelsLayer client={client} />}
        {/* {client.parcel_data_enabled && parcelDataVisible && <MapParcelsLayer client={client} />} */}
        <MapGeographiesLayer client={client!} />
        <MapBuildingsLayer />
        <MapPropertyClustersLayer properties={properties} />

        {currentMapView === MapView.Map ? (
          <div className="absolute bottom-0 left-0 top-18 flex overflow-x-hidden">
            <MapCardsList
              listings={listings}
              isError={isError}
              isLoading={isLoading}
              isFetching={isFetching}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
            />
            <Outlet />
          </div>
        ) : (
          <Outlet
            context={
              {
                listings,
                isError,
                isLoading,
                isFetching,
                fetchNextPage,
                hasNextPage,
              } as MapOutletContextData
            }
          />
        )}
      </ListingsMap>
    </main>
  )
}
